import { navigate } from '@reach/router';
import { linkTo } from 'utils/links';
import { t } from '@lingui/macro';
import Config from 'config';
import Connection from 'utils/connection';
import JSONReader from 'utils/reader';
import Types from 'utils/types';
import uuid from 'utils/uuid';
import i18n from 'config/i18n';

import { showMessage } from 'store/modules/app/actions';

const client = new Connection();

const fetchMiddleware = ({ dispatch, getState }) => next => (action) => {
  if (typeof action === 'function') {
    return action(dispatch, getState);
  }

  const { promise, types, ...rest } = action; // eslint-disable-line no-redeclare

  if (!promise) {
    return next(action);
  }

  const token = getState().auth && getState().auth.token;
  const requestId = uuid();

  client.setToken(token);
  next({ ...rest, type: types.REQUEST, requestId });

  promise.method = (promise.method && promise.method.toLowerCase()) || 'get';
  promise.host = promise.host || Config.api.url;

  const actionPromise = client[promise.method](promise)
    .then(payload => Promise.resolve({ payload }));

  actionPromise
    .then(response => {
      let data = response;
      // Check if there's a reader for this response so we can
      // normalize the data based on the given model
      if (promise.reader) {
        // We can send a config object to read most common the responses
        if (Types.isObject(promise.reader)) {
          const normalized = JSONReader(promise.reader, data.payload);
          data = {
            ...data,
            payload: {
              ...data.payload,
              [promise.reader.root]: normalized,
            },
          };
        } else if (Types.isFunction(promise.reader)) {
          // Or it can also be a custom function to read complex responses
          data = promise.reader(response.payload);
        }
      }

      next({ ...rest, ...data, type: types.SUCCESS, requestId });
    })
    .catch((error) => {
      if (Config.environment === 'development') {
        console.error(error); // eslint-disable-line no-console
      }

      // UI expects an errors object, if is not there
      // we sould show a generic error
      if (!error.data?.errors) {
        if (!error.data) {
          error.data = {};
        }
        error.data.errors = {
          isDefaultMessage: true,
          status: error.status,
          [error.status]: getGenericServerError(error.status),
        };
      }

      next({ ...rest, payload: error.data, type: types.FAIL, requestId, code: error.status });

      // If not authenticated redirect to login page
      if (error.data && error.data.error === 'token_not_provided') {
        return navigate(linkTo('/auth/sign-in'));
      }

      // If token expired redirect to login page
      if (error.status === 401 && error.data && error.data.message === 'Unauthenticated.') {
        return navigate(linkTo('/auth/sign-in'));
      }

      // If we need to show paywall
      if (error.status === 499) {
        if (error?.data?.errors) {
          dispatch(showMessage({ type: 'error', message: error?.data?.errors?.join(',') }));
        }
        return navigate(linkTo('/app/paywall'));
      }
    });

  return actionPromise;
};

const messages = {
  '500': i18n._(t`There was an error, please try again and see if that works.`),
  '404': i18n._(t`The resource you are looking doesn't exist.`),
  '403': i18n._(t`Seems like you have no access to this resource.`),
  'default': i18n._(t`Seems like there was an unknown error, please let us know!`),
};
function getGenericServerError(status) {
  return messages[status] || messages.default;
}

export default fetchMiddleware;